import {
  FLOATING_BUTTONS_HIDDEN_CLASS,
  FLOATING_BUTTONS_TRANSITION,
  useFloatingButtonsLift
} from "@/components/support/useFloatingButtonsLift";
import { useSellerCarts } from "@/queries/cart";
import { formatPrice } from "@/utils/money";
import { pluralCount } from "@/utils/text";
import { ShoppingCart } from "@medusajs/icons";
import { cn } from "@repo/design-system/utils";
import { Link, useLocation } from "@tanstack/react-router";

/**
 * Pages that ship their own phone bottom bar (the cart's Proceed-to-checkout
 * summary bar and checkout's step-action bar, KAN-73/KAN-77). This browse bar
 * hides on them so a phone never shows two stacked bottom bars.
 */
const OWN_BAR_PREFIXES = ["/cart", "/checkout"];

/**
 * The phone-only cart bar: `N items · $total → View cart`, pinned under the
 * content so the cart stays one tap away while browsing.
 *
 * It is the phone's replacement for the desktop rail and the tablet slide-over
 * - below `md` neither of those exists - and it deliberately navigates to the
 * full cart page rather than opening a sheet: on a phone the cart page *is* the
 * full-screen surface, and a drawer over a 360px viewport buys nothing.
 * See storefront#145 (P4).
 *
 * Renders nothing on an empty cart (a permanent "0 items" bar spends ~56px of a
 * phone screen saying there is nothing to see) or on the cart/checkout pages,
 * which carry their own bottom bar.
 */
export function StickyCartBar() {
  const { data } = useSellerCarts();
  const pathname = useLocation({ select: (location) => location.pathname });
  const itemCount = data?.summary.total_items ?? 0;
  const barRef = useFloatingButtonsLift<HTMLAnchorElement>();

  const onOwnBarPage = OWN_BAR_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );

  if (itemCount === 0 || onOwnBarPage) {
    return null;
  }

  return (
    <Link
      to="/cart"
      // The support FABs ride above this bar rather than over its right edge.
      ref={barRef}
      // Out of sight (but still taking its space, so nothing jumps) while a
      // drawer that sets the html attribute is open: the order assistant and
      // every `DrawerPanel`. Those panels are inset from the viewport edge, so
      // the bar would otherwise show in the margin around them. Same classes and
      // `duration-300` as the floating buttons, so the two hide and return together.
      className={cn(
        "flex min-h-[65px] flex-shrink-0 items-center justify-between gap-3 border-t border-purple-700 bg-purple-600 px-4 py-3 text-sm font-medium text-white duration-300 hover:bg-purple-700 motion-reduce:duration-0 md:hidden",
        FLOATING_BUTTONS_TRANSITION,
        FLOATING_BUTTONS_HIDDEN_CLASS
      )}
    >
      <span className="flex min-w-0 items-center gap-2">
        <ShoppingCart className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <span className="truncate">
          {pluralCount(itemCount, "item")} ·{" "}
          {formatPrice(data?.summary.grand_total ?? 0)}
        </span>
      </span>
      <span className="flex-shrink-0">View cart →</span>
    </Link>
  );
}
